<template>
  <div class="FreeShippingTooltip">
    <button
      ref="tooltipButton"
      class="FreeShippingTooltip__Button"
      aria-label="Free shipping information"
      aria-describedby="free-shipping-tooltip-content"
      @click="toggleTooltip($event)"
      @mouseenter="toggleTooltip($event)"
      @mouseleave="toggleTooltip($event)"
    >
      <Icon :icon="'info-tooltip'" :aria-hidden="true" />
    </button>
    <!-- Tooltip content with dynamic positioning from Floating UI -->
    <!-- data-placement controls arrow CSS styles (top/bottom/left/right) -->
    <div
      id="free-shipping-tooltip-content"
      ref="tooltipContent"
      class="FreeShippingTooltip__Content"
      role="tooltip"
      :aria-hidden="tooltipToggle"
      :style="floatingStyles"
      :data-placement="arrowSide"
    >
      <!-- Arrow element positioned by Floating UI's arrow middleware -->
      <!-- Arrow direction is styled via ::before and ::after in CSS based on data-placement -->
      <div
        ref="tooltipArrow"
        class="FreeShippingTooltip__Arrow"
        :style="arrowStyles"
      ></div>
      <p class="p6">{{ text }}</p>
      <button
        aria-label="Close tooltip"
        class="FreeShippingTooltip__Close"
        @click="toggleTooltip($event)"
      >
        <Icon :icon="'close-tooltip'" :aria-hidden="true" />
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
// Import Floating UI utilities including 'arrow' for tooltip arrow positioning
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  arrow,
} from '@floating-ui/vue';
import Icon from './Icon.vue';

const props = defineProps({
  text: { type: String, required: true },
  placement: { type: String, default: 'bottom' },
});

const tooltipToggle = ref(true);
const clickedEvent = ref(false);

// Floating UI setup - handles tooltip positioning and arrow alignment
const tooltipButton = ref(null); // Reference to the info button
const tooltipContent = ref(null); // Reference to the tooltip content container
const tooltipArrow = ref(null); // Reference to the arrow element inside tooltip
const variables = window.getComputedStyle(document.body);
const navOffset = variables.getPropertyValue('--navOffset').replace('px', '');

// Configure Floating UI with positioning middleware
// - placement: Extract placement data to determine which side the tooltip appears on
// - middlewareData: Contains calculated arrow position from the arrow middleware
const {
  floatingStyles,
  placement: currentPlacement,
  middlewareData,
} = useFloating(tooltipButton, tooltipContent, {
  placement: props.placement, // Controlled via prop, defaults to 'bottom'
  middleware: [
    offset(16), // 16px gap between button and tooltip (includes space for arrow + margin)
    flip({ padding: parseInt(navOffset) }), // Automatically flip to opposite side if there's not enough space
    shift({ padding: 8 }), // Keep tooltip within viewport with 8px padding
    arrow({ element: tooltipArrow }), // Calculate optimal arrow position
  ],
  whileElementsMounted: autoUpdate, // Update position when elements change (scroll, resize, etc.)
});

// Extract the primary placement side (top/bottom/left/right) from placement value
// Used to apply the correct CSS styles for arrow direction via data-placement attribute
const arrowSide = computed(() => {
  return currentPlacement.value.split('-')[0];
});

// Calculate arrow position styles based on Floating UI's arrow middleware data
// The arrow middleware returns optimal x/y coordinates for centering the arrow
const arrowStyles = computed(() => {
  const arrowData = middlewareData.value.arrow;

  if (!arrowData) return {};

  const { x, y } = arrowData;

  // Offset by half the arrow width (8px) to center the arrow's point over the button
  // Without this offset, the left edge of the arrow would be centered instead of the point
  const offsetX = x != null ? x - 8 : null;
  const offsetY = y != null ? y - 8 : null;

  return {
    left: offsetX != null ? `${offsetX}px` : '',
    top: offsetY != null ? `${offsetY}px` : '',
  };
});

/**
 * Toggles tooltip visibility based on interaction type.
 * Supports click (sticky toggle), mouseenter (hover open), and mouseleave (hover close).
 * Click-opened tooltips remain open until explicitly closed by another click.
 * @param {MouseEvent} e - The triggering mouse event
 */
const toggleTooltip = (e) => {
  e.preventDefault();
  switch (e.type) {
    case 'click':
      if (!tooltipToggle.value && !clickedEvent.value) {
        tooltipToggle.value = false;
        clickedEvent.value = true;
        break;
      } else {
        tooltipToggle.value = !tooltipToggle.value;
        clickedEvent.value = !clickedEvent.value;
        break;
      }
    case 'mouseenter':
      if (!clickedEvent.value) {
        tooltipToggle.value = false;
        break;
      }
    case 'mouseleave':
      if (!clickedEvent.value && !tooltipToggle.value) {
        tooltipToggle.value = true;
        break;
      }
  }
};
</script>
